import {Controller} from "@hotwired/stimulus"

/**
 * Bag Checkout Form Validator (Native)
 * 
 * Uses native browser Constraint Validation API for simplicity and reliability.
 * No external libraries, no detached element issues, works perfectly in Safari.
 */
export default class extends Controller {
    connect() {
        //console.log('BagCheckoutFormValidator connected (native validation)');
        
        // Track if submit button was clicked (for Safari compatibility)
        this.submitButtonClicked = false;

        // Setup native validation
        this.initializeNativeValidation();
    }

    initializeNativeValidation() {
        const form = this.element;
        if (!form) {
            console.error('Bag checkout form element not found');
            return;
        }

        // Prevent default HTML5 validation UI (we'll style it ourselves)
        form.setAttribute('novalidate', 'novalidate');

        // Track submit button clicks for Safari compatibility
        const submitButton = form.querySelector('[type="submit"]');
        if (submitButton) {
            submitButton.addEventListener('click', (e) => {
                this.submitButtonClicked = true;
                //console.log('Submit button clicked - flag set');
            });
        }

        // Handle form submission
        form.addEventListener('submit', (e) => {
            // Only actually submit if submit button was clicked
            if (!this.submitButtonClicked) {
                //console.log('Form submit prevented - not triggered by submit button');
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
            
            // Validate all visible fields before allowing submission
            const isValid = this.validateVisibleFields();
            
            if (!isValid) {
                //console.log('Form is INVALID - preventing submission');
                e.preventDefault();
                e.stopPropagation();
                this.submitButtonClicked = false;
                return false;
            }
            
            //console.log('Form is VALID - allowing submission');
            this.submitButtonClicked = false;
            // Let the form submit naturally (don't call form.submit())
            return true;
        });

        // Store validation helper on form for other controllers to use
        form._nativeValidator = this;

        // Add real-time validation on blur
        this.setupRealtimeValidation(form);

        //console.log('BagCheckout Form Validator initialized successfully (native)');
    }

    /**
     * Normalize validation error messages
     * @param {string} message - The browser's validation message
     * @returns {string} Normalized message
     */
    normalizeErrorMessage(message) {
        // Replace common browser messages with cleaner versions
        if (!message || message === '') return 'Required Field';
        if (message.toLowerCase().includes('fill out this field')) return 'Required Field';
        if (message.toLowerCase().includes('please fill')) return 'Required Field';
        return message;
    }

    /**
     * Validate all visible fields in the form
     * @returns {boolean} True if all visible fields are valid
     */
    validateVisibleFields() {
        const form = this.element;
        let allValid = true;
        let firstInvalidField = null;

        // Get all input/select/textarea elements with validation
        const fields = form.querySelectorAll('input, select, textarea');
        
        fields.forEach(field => {
            // Skip fields that should not be validated
            if (this.shouldSkipField(field)) {
                // Remove any validation styling from hidden fields
                field.classList.remove('is-invalid', 'is-valid');
                return;
            }

            // Use custom validation for credit card fields, native for others
            let isValid = true;
            let errorMessage = '';

            if (field.name === 'order[cc_number]') {
                const result = this.validateCreditCardNumber(field.value);
                isValid = result.valid;
                errorMessage = result.error;
            } else if (field.name === 'order[cc_month]' || field.name === 'order[cc_year]') {
                const result = this.validateExpirationDate();
                isValid = result.valid;
                errorMessage = result.error;
            } else if (field.name === 'order[cc_code]') {
                const result = this.validateCVV(field.value);
                isValid = result.valid;
                errorMessage = result.error;
            } else {
                // Use native validation
                isValid = field.checkValidity();
                errorMessage = this.normalizeErrorMessage(field.validationMessage);
            }
            
            if (!isValid) {
                field.classList.add('is-invalid');
                allValid = false;
                
                // Track first invalid field for focus
                if (!firstInvalidField) {
                    firstInvalidField = field;
                }
                
                // Show error message
                this.showFieldError(field, errorMessage);
            } else {
                field.classList.remove('is-invalid');
                this.hideFieldError(field);
            }
        });

        // Focus on first invalid field
        if (firstInvalidField) {
            firstInvalidField.focus();
            // Scroll to field
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return allValid;
    }

    /**
     * Validate a single field
     * @param {string} fieldName - Name attribute of the field
     * @returns {boolean} True if field is valid
     */
    validateField(fieldName) {
        const field = this.element.querySelector(`[name="${fieldName}"]`);
        
        if (!field || this.shouldSkipField(field)) {
            return true;
        }

        // Use custom validation for credit card fields
        let isValid = true;
        let errorMessage = '';

        if (fieldName === 'order[cc_number]') {
            const result = this.validateCreditCardNumber(field.value);
            isValid = result.valid;
            errorMessage = result.error;
        } else if (fieldName === 'order[cc_month]' || fieldName === 'order[cc_year]') {
            const result = this.validateExpirationDate();
            isValid = result.valid;
            errorMessage = result.error;
        } else if (fieldName === 'order[cc_code]') {
            const result = this.validateCVV(field.value);
            isValid = result.valid;
            errorMessage = result.error;
        } else {
            // Use native validation for other fields
            isValid = field.checkValidity();
            errorMessage = this.normalizeErrorMessage(field.validationMessage);
        }
        
        if (!isValid) {
            field.classList.add('is-invalid');
            this.showFieldError(field, errorMessage);
            return false;
        } else {
            field.classList.remove('is-invalid');
            this.hideFieldError(field);
            return true;
        }
    }

    /**
     * Determine if a field should be skipped during validation
     * @param {HTMLElement} field 
     * @returns {boolean}
     */
    shouldSkipField(field) {
        // Skip if no name attribute
        if (!field.name) {
            return true;
        }

        // Skip disabled fields
        if (field.disabled) {
            return true;
        }

        // Skip fields in inactive sections
        const section = field.closest('.checkout-section');
        if (section) {
            const isActive = section.classList.contains('active');
            const isCompleted = section.classList.contains('completed');
            if (!isActive && !isCompleted) {
                return true;
            }
        }

        // Skip billing fields if billing form is hidden
        if (field.name.includes('bill_')) {
            const billingForm = field.closest('#billing-address-form');
            if (billingForm) {
                const isHidden = window.getComputedStyle(billingForm).display === 'none';
                if (isHidden) {
                    return true;
                }
            }
        }

        // Skip fields in hidden payment method sections
        const paymentContent = field.closest('.payment-method-content');
        if (paymentContent) {
            const isHidden = window.getComputedStyle(paymentContent).display === 'none';
            if (isHidden) {
                return true;
            }
        }

        // Skip if explicitly marked
        if (field.dataset.validate === 'false') {
            return true;
        }

        return false;
    }

    /**
     * Validate credit card number using Luhn algorithm
     * @param {string} cardNumber 
     * @returns {object} {valid: boolean, error: string}
     */
    validateCreditCardNumber(cardNumber) {
        // Remove spaces and dashes
        const cleaned = (cardNumber || '').replace(/[\s-]/g, '');
        
        if (!cleaned) {
            return { valid: false, error: 'Credit card number is required' };
        }
        
        if (!/^\d+$/.test(cleaned)) {
            return { valid: false, error: 'Credit card number must contain only digits' };
        }
        
        if (cleaned.length < 13 || cleaned.length > 19) {
            return { valid: false, error: 'Credit card number must be 13-19 digits' };
        }
        
        // Luhn algorithm
        let sum = 0;
        let isEven = false;
        
        for (let i = cleaned.length - 1; i >= 0; i--) {
            let digit = parseInt(cleaned[i], 10);
            
            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }
            
            sum += digit;
            isEven = !isEven;
        }
        
        if (sum % 10 !== 0) {
            return { valid: false, error: 'Invalid credit card number' };
        }
        
        return { valid: true, error: '' };
    }

    /**
     * Validate expiration date (must be in the future)
     * @returns {object} {valid: boolean, error: string}
     */
    validateExpirationDate() {
        const monthField = this.element.querySelector('[name="order[cc_month]"]');
        const yearField = this.element.querySelector('[name="order[cc_year]"]');
        
        if (!monthField || !yearField) {
            return { valid: true, error: '' };
        }
        
        const month = monthField.value;
        const year = yearField.value;
        
        if (!month || month === '' || month === 'Month') {
            return { valid: false, error: 'Expiration month is required' };
        }
        
        if (!year || year === '' || year === 'Year') {
            return { valid: false, error: 'Expiration year is required' };
        }
        
        // Check if date is in the future
        const now = new Date();
        const currentMonth = now.getMonth() + 1; // 0-indexed
        const currentYear = now.getFullYear();
        
        const expiryMonth = parseInt(month, 10);
        const expiryYear = parseInt(year, 10);
        
        if (expiryYear < currentYear || (expiryYear === currentYear && expiryMonth < currentMonth)) {
            return { valid: false, error: 'Card has expired' };
        }
        
        return { valid: true, error: '' };
    }

    /**
     * Validate CVV (3-4 digits)
     * @param {string} cvv 
     * @returns {object} {valid: boolean, error: string}
     */
    validateCVV(cvv) {
        const cleaned = (cvv || '').trim();
        
        if (!cleaned) {
            return { valid: false, error: 'CVV is required' };
        }
        
        if (!/^\d{3,4}$/.test(cleaned)) {
            return { valid: false, error: 'CVV must be 3-4 digits' };
        }
        
        return { valid: true, error: '' };
    }

    /**
     * Show error message for a field
     * @param {HTMLElement} field 
     * @param {string} customMessage - Optional custom error message
     */
    showFieldError(field, customMessage = null) {
        const container = field.closest('.fv-row') || field.parentElement;
        let feedback = container.querySelector('.invalid-feedback');
        
        // Generate unique ID for error message
        const errorId = `error-${field.name || field.id || Math.random().toString(36).substr(2, 9)}`;
        
        if (!feedback) {
            feedback = document.createElement('div');
            feedback.className = 'invalid-feedback';
            feedback.id = errorId;
            feedback.style.display = 'block';
            feedback.setAttribute('role', 'alert');
            container.appendChild(feedback);
        } else {
            feedback.id = errorId;
        }
        
        // Use provided message, or field's dataset, or browser validation message
        const message = customMessage || field.dataset.errorMessage || this.normalizeErrorMessage(field.validationMessage) || 'Required Field';
        feedback.textContent = message;
        feedback.style.display = 'block';
        
        // Associate error message with field using aria-describedby
        field.setAttribute('aria-describedby', errorId);
        field.setAttribute('aria-invalid', 'true');
    }

    /**
     * Hide error message for a field
     * @param {HTMLElement} field 
     */
    hideFieldError(field) {
        const container = field.closest('.fv-row') || field.parentElement;
        const feedback = container.querySelector('.invalid-feedback');
        
        if (feedback) {
            feedback.style.display = 'none';
        }
        
        // Remove ARIA attributes
        field.removeAttribute('aria-describedby');
        field.removeAttribute('aria-invalid');
    }

    /**
     * Setup real-time validation on blur
     * @param {HTMLFormElement} form 
     */
    setupRealtimeValidation(form) {
        const fields = form.querySelectorAll('input, select, textarea');
        
        fields.forEach(field => {
            // Validate on blur
            field.addEventListener('blur', () => {
                if (!this.shouldSkipField(field)) {
                    let isValid = true;
                    let errorMessage = '';

                    // Custom validation for credit cards
                    if (field.name === 'order[cc_number]') {
                        const result = this.validateCreditCardNumber(field.value);
                        isValid = result.valid;
                        errorMessage = result.error;
                    } else if (field.name === 'order[cc_month]' || field.name === 'order[cc_year]') {
                        // Only validate expiration if BOTH fields have values
                        const monthField = this.element.querySelector('[name="order[cc_month]"]');
                        const yearField = this.element.querySelector('[name="order[cc_year]"]');
                        const month = monthField.value;
                        const year = yearField.value;
                        
                        if (month && month !== 'Month' && year && year !== 'Year') {
                            const result = this.validateExpirationDate();
                            isValid = result.valid;
                            errorMessage = result.error;
                        } else {
                            // Don't validate on blur if both fields aren't filled yet
                            return;
                        }
                    } else if (field.name === 'order[cc_code]') {
                        const result = this.validateCVV(field.value);
                        isValid = result.valid;
                        errorMessage = result.error;
                    } else {
                        isValid = field.checkValidity();
                        errorMessage = this.normalizeErrorMessage(field.validationMessage);
                    }
                    
                    if (!isValid) {
                        field.classList.add('is-invalid');
                        this.showFieldError(field, errorMessage);
                    } else {
                        field.classList.remove('is-invalid');
                        this.hideFieldError(field);
                    }
                }
            });

            // Clear error on input (while typing)
            field.addEventListener('input', () => {
                // Skip revalidation for email fields on input to prevent browser issues
                // Email will be validated on blur instead
                if (field.type === 'email') {
                    return;
                }
                
                if (field.classList.contains('is-invalid')) {
                    // Re-validate
                    let isValid = true;
                    let errorMessage = '';

                    if (field.name === 'order[cc_number]') {
                        const result = this.validateCreditCardNumber(field.value);
                        isValid = result.valid;
                        errorMessage = result.error;
                    } else if (field.name === 'order[cc_month]' || field.name === 'order[cc_year]') {
                        // Only validate expiration if BOTH fields have values
                        const monthField = this.element.querySelector('[name="order[cc_month]"]');
                        const yearField = this.element.querySelector('[name="order[cc_year]"]');
                        const month = monthField.value;
                        const year = yearField.value;
                        
                        if (month && month !== 'Month' && year && year !== 'Year') {
                            const result = this.validateExpirationDate();
                            isValid = result.valid;
                            errorMessage = result.error;
                        } else {
                            // Clear the error if one field is empty
                            field.classList.remove('is-invalid');
                            this.hideFieldError(field);
                            return;
                        }
                    } else if (field.name === 'order[cc_code]') {
                        const result = this.validateCVV(field.value);
                        isValid = result.valid;
                        errorMessage = result.error;
                    } else {
                        isValid = field.checkValidity();
                        errorMessage = this.normalizeErrorMessage(field.validationMessage);
                    }
                    
                    if (isValid) {
                        field.classList.remove('is-invalid');
                        this.hideFieldError(field);
                    } else {
                        this.showFieldError(field, errorMessage);
                    }
                }
            });

            // Also validate expiration fields when either month or year changes
            if (field.name === 'order[cc_month]' || field.name === 'order[cc_year]') {
                field.addEventListener('change', () => {
                    // Validate both month and year fields
                    const monthField = this.element.querySelector('[name="order[cc_month]"]');
                    const yearField = this.element.querySelector('[name="order[cc_year]"]');
                    
                    const month = monthField.value;
                    const year = yearField.value;
                    
                    // Only validate if BOTH fields have values
                    // This prevents showing "year required" error when selecting month first
                    if (month && month !== 'Month' && year && year !== 'Year') {
                        const result = this.validateExpirationDate();
                        
                        if (!result.valid) {
                            // Show error on both fields since it's a combined validation
                            monthField.classList.add('is-invalid');
                            yearField.classList.add('is-invalid');
                            this.showFieldError(monthField, result.error);
                            this.showFieldError(yearField, result.error);
                        } else {
                            // Clear errors from both fields if valid
                            monthField.classList.remove('is-invalid');
                            yearField.classList.remove('is-invalid');
                            this.hideFieldError(monthField);
                            this.hideFieldError(yearField);
                        }
                    } else {
                        // If only one field is filled, clear any previous errors
                        // Don't validate yet - wait for both fields
                        monthField.classList.remove('is-invalid');
                        yearField.classList.remove('is-invalid');
                        this.hideFieldError(monthField);
                        this.hideFieldError(yearField);
                    }
                });
            }
        });
    }

    /**
     * Reset validation state
     */
    resetValidation() {
        const fields = this.element.querySelectorAll('input, select, textarea');
        fields.forEach(field => {
            field.classList.remove('is-invalid');
            this.hideFieldError(field);
        });
    }

    disconnect() {
        // Cleanup if needed
    }
}
